/**
 * MelodyContour
 * P2 FIX: Enhanced melody contour with goal-directed motion
 * 
 * Key improvements:
 * - Phrase-level climax targeting (build toward peak, then descend)
 * - Register bounds memory (force return when at extremes)
 * - Inter-phrase contrast (peak high -> start low next phrase)
 */
export class MelodyContour {
    private melodyDirection: number = 1;
    private melodyContour: 'ascending' | 'descending' | 'arch' | 'wave' | 'random' = 'random';
    private lastStepSize: number = 1;

    public touchCount: number = 0;

    // P2 FIX: Phrase-level climax tracking
    private phraseStartIndex: number = 0;
    private phraseTargetPeakIndex: number = 0;  // Where the phrase should peak
    private currentPhrasePosition: number = 0;  // 0-1 within phrase
    private lastPhraseEndedHigh: boolean = false;  // For inter-phrase contrast

    // P2 FIX: Register bounds (prevent melody from drifting too high/low)
    private currentRegisterPosition: number = 0.5;  // 0 = low, 1 = high (normalized)
    private readonly REGISTER_LOW_THRESHOLD = 0.2;
    private readonly REGISTER_HIGH_THRESHOLD = 0.8;
    private forceReturnToCenter: boolean = false;

    constructor() {
        this.reset();
    }

    public reset() {
        this.melodyDirection = 1;
        this.melodyContour = 'random';
        this.lastStepSize = 1;
        this.touchCount = 0;

        // P2 FIX: Reset phrase tracking
        this.phraseStartIndex = 0;
        this.phraseTargetPeakIndex = 0;
        this.currentPhrasePosition = 0;
        this.lastPhraseEndedHigh = false;
        this.currentRegisterPosition = 0.5;
        this.forceReturnToCenter = false;
    }

    public randomizeParams(mood: 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery') {
        switch (mood) {
            case 'Happy':
            case 'Energetic':
                this.melodyContour = 'ascending';
                this.melodyDirection = 1;
                break;
            case 'Sad':
                this.melodyContour = 'descending';
                this.melodyDirection = -1;
                break;
            case 'Calm':
                this.melodyContour = 'wave';
                this.melodyDirection = 1;
                break;
            case 'Mystery':
                this.melodyContour = 'random';
                this.melodyDirection = Math.random() < 0.5 ? 1 : -1;
                break;
            default:
                this.melodyContour = 'random';
        }
    }

    /**
     * P2 FIX: Update phrase position for goal-directed motion
     * @param phrasePosition - 0 to 1, where in the phrase we are
     * @param phraseLength - Total phrase length for peak calculation
     */
    public updatePhrasePosition(phrasePosition: number, phraseLength: number = 8): void {
        this.currentPhrasePosition = phrasePosition;

        // Peak should occur around 60-75% through the phrase (golden ratio)
        this.phraseTargetPeakIndex = Math.floor(phraseLength * 0.67);
    }

    /**
     * P2 FIX: Signal a new phrase is starting (for inter-phrase contrast)
     */
    public onPhraseStart(endedHigh: boolean): void {
        this.lastPhraseEndedHigh = endedHigh;
        this.currentPhrasePosition = 0;

        // Inter-phrase contrast: if last phrase peaked high, start this one low
        if (endedHigh) {
            this.melodyDirection = -1;
        } else {
            this.melodyDirection = 1;
        }
    }

    /**
     * P2 FIX: Update register position to track where melody is in the range
     * @param currentNoteIndex - Current note index in the scale
     * @param scaleLength - Total length of usable scale notes
     */
    public updateRegisterPosition(currentNoteIndex: number, scaleLength: number): void {
        if (scaleLength <= 0) return;

        this.currentRegisterPosition = currentNoteIndex / scaleLength;

        // Check if we need to force return to center
        if (this.currentRegisterPosition <= this.REGISTER_LOW_THRESHOLD) {
            this.forceReturnToCenter = true;
            this.melodyDirection = 1;  // Force upward
        } else if (this.currentRegisterPosition >= this.REGISTER_HIGH_THRESHOLD) {
            this.forceReturnToCenter = true;
            this.melodyDirection = -1;  // Force downward
        } else if (this.currentRegisterPosition > 0.35 && this.currentRegisterPosition < 0.65) {
            // Back in safe center zone
            this.forceReturnToCenter = false;
        }
    }

    /**
     * P2 FIX: Check if we need to force return to center register
     */
    public needsRegisterCorrection(): boolean {
        return this.forceReturnToCenter;
    }

    public updateContraryMotion(currentBassNote: number | null, lastBassNote: number | null) {
        // Don't override if we're forcing register correction
        if (this.forceReturnToCenter) return;

        if (currentBassNote !== null && lastBassNote !== null) {
            const bassInterval = currentBassNote - lastBassNote;
            if (bassInterval > 3) {
                this.melodyDirection = -1;
                if (this.melodyContour === 'ascending') this.melodyContour = 'random';
            } else if (bassInterval < -3) {
                this.melodyDirection = 1;
                if (this.melodyContour === 'descending') this.melodyContour = 'random';
            }
        }
    }

    public getStepSize(): number {
        const wasLastMoveALeap = this.lastStepSize > 2;
        const roll = Math.random();

        let step = 1;

        // P2 FIX: When forcing register return, use smaller steps
        if (this.forceReturnToCenter) {
            step = roll < 0.6 ? 1 : 2;
            this.lastStepSize = step;
            return step;
        }

        if (wasLastMoveALeap) {
            // If last move was a leap, strongly favor stepwise motion
            if (roll < 0.70) step = 1;
            else if (roll < 0.90) step = 2;
            else step = 3;
        } else {
            // Normal weighted distribution
            switch (this.melodyContour) {
                case 'ascending':
                case 'descending':
                    if (roll < 0.45) step = 1;
                    else if (roll < 0.75) step = 2;
                    else if (roll < 0.90) step = 3;
                    else step = 4;
                    break;
                case 'arch':
                case 'wave':
                    if (roll < 0.35) step = 1;
                    else if (roll < 0.60) step = 2;
                    else if (roll < 0.80) step = 3;
                    else if (roll < 0.92) step = 4;
                    else step = 5;
                    break;
                case 'random':
                default:
                    if (roll < 0.30) step = 1;
                    else if (roll < 0.50) step = 2;
                    else if (roll < 0.70) step = 3;
                    else if (roll < 0.85) step = 4;
                    else if (roll < 0.95) step = 5;
                    else step = Math.floor(Math.random() * 3) + 5;
                    break;
            }
        }

        this.lastStepSize = step;
        return step;
    }

    /**
     * P2 FIX: Get direction with goal-directed biasing
     * - Before peak (0-67%): bias upward
     * - At/after peak (67%+): bias downward toward resolution
     */
    public getDirection(): number {
        // Register correction takes priority
        if (this.forceReturnToCenter) {
            return this.melodyDirection;
        }

        // P2 FIX: Phrase-based direction biasing
        if (this.currentPhrasePosition < 0.67) {
            // Building toward climax - bias upward
            return Math.random() < 0.65 ? 1 : -1;
        } else {
            // Descending toward resolution - bias downward
            return Math.random() < 0.65 ? -1 : 1;
        }
    }

    /**
     * P2 FIX: Check if current position is the climax point
     */
    public isAtClimaxPoint(): boolean {
        return this.currentPhrasePosition >= 0.60 && this.currentPhrasePosition <= 0.75;
    }

    /**
     * P2 FIX: Get a recommended step for the climax moment (usually a leap up)
     */
    public getClimaxStep(): { step: number; direction: number } {
        return { step: 3, direction: 1 };  // Skip up for dramatic effect
    }
}
